<template>
  <h1 id="cookies-policy">Cookies Policy</h1>
  <p><strong>Last Updated 9th October 2025</strong></p>
  <h2 id="1-what-are-cookies">1. What are cookies</h2>
  <p>
    Cookies are small text files that are downloaded and stored on the User’s
    device (computer, smartphone, or tablet) when accessing certain websites or
    applications. Their purpose is to enhance the browsing experience by
    allowing the Website to remember preferences and settings, thereby
    facilitating faster and more efficient interactions. The information
    collected by cookies is anonymous and does not contain any sensitive
    information, as they do not collect data that could personally identify the
    User. In any case, the User can access their browser settings to modify
    and/or block the installation of Cookies sent from the Website, without this
    preventing access to the content.
  </p>
  <h2 id="2-what-does-mappr-use-cookies-for">
    2. What does MAPPR use cookies for
  </h2>
  <p>
    In accordance with Article 22 of the Law on Information Society Services and
    Electronic Commerce (LSSI-CE) and Recital (30) of the General Data
    Protection Regulation (hereinafter, “<strong>GDPR</strong>”), this Website
    uses cookies and other storage and retrieval technologies to track User
    interaction with the services offered. Cookies enable the Website to
    recognise the browser and device type used to access it, thereby
    facilitating navigation and optimising the User experience.
    <strong>MAPPR uses cookies for the following purposes:</strong>
  </p>
  <h2 id="essential-cookies">
    <strong>Essential Cookies</strong>
  </h2>
  <p>
    Strictly necessary cookies to provide a service or content explicitly
    requested by the User. These manage technical aspects such as login
    identification, user preferences, interaction tools, and content access
    localisation.
  </p>
  <figure class="table-figure">
    <table>
      <thead>
        <tr>
          <th>Name</th>
          <th>Purpose</th>
          <th>Expiry</th>
          <th>Supplier</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>__cf_bm</td>
          <td>read and filter requests from bots</td>
          <td>24 hours</td>
          <td>mappr.com</td>
        </tr>
        <tr>
          <td>ph_phc*</td>
          <td>
            Creates a distinct anonymous user id for server/client
            communication.
          </td>
          <td>Session</td>
          <td>mappr.com</td>
        </tr>
        <tr>
          <td>__stripe_mid</td>
          <td>Set for fraud prevention purposes</td>
          <td>1 year</td>
          <td>mappr.com</td>
        </tr>
        <tr>
          <td>sb-api-auth-token</td>
          <td>
            Authentication Token allowing the user to login and out of mappr.com
          </td>
          <td>14 months</td>
          <td>mappr.com</td>
        </tr>
        <tr>
          <td>__Secure-has_logged_in</td>
          <td>Used to authenticate with a user’s stripe data</td>
          <td>6 months</td>
          <td>stripe.com</td>
        </tr>
        <tr>
          <td>__stripe_orig_props</td>
          <td>
            Remembers how you got to our website, including the URL you came
            from
          </td>
          <td>1 year</td>
          <td>stripe.com</td>
        </tr>
        <tr>
          <td>cookie-perms</td>
          <td>Stores a user&#39;s cookie permissions</td>
          <td>2 months</td>
          <td>stripe.com</td>
        </tr>
        <tr>
          <td>machine_identifier</td>
          <td>Set for fraud prevention purposes</td>
          <td>1 month</td>
          <td>stripe.com</td>
        </tr>
        <tr>
          <td>private_machine_identifier</td>
          <td>
            Identifies the computer across login sessions and users to prevent
            fraud
          </td>
          <td>1 year</td>
          <td>stripe.com</td>
        </tr>
        <tr>
          <td>site-auth</td>
          <td>
            Remembers whether you&#39;re logged in to the Stripe Dashboard
          </td>
          <td>Session</td>
          <td>stripe.com</td>
        </tr>
        <tr>
          <td>stripe.csrf</td>
          <td>
            Prevents attackers from making requests that change user data to
            Stripe from other websites
          </td>
          <td>Session</td>
          <td>stripe.com</td>
        </tr>
        <tr>
          <td>m</td>
          <td>
            Tests whether sessionStorage is available in the browser, or whether
            fallback mechanisms are needed for basic functions
          </td>
          <td>Session</td>
          <td>stripe.com</td>
        </tr>
      </tbody>
    </table>
  </figure>
  <h2 id="functional-cookies">Functional Cookies</h2>
  <p>
    Functional cookies provide the user with information that enhances the
    browsing experience, such as storing the language the user has chosen.
  </p>
  <figure class="table-figure">
    <table>
      <thead>
        <tr>
          <th>Name</th>
          <th>Purpose</th>
          <th>Expiry</th>
          <th>Supplier</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>i18n_redirected</td>
          <td>
            Stores the users language selection. When returning to mappr.com
            it’ll have the same language you set in your last visit
          </td>
          <td>1 year</td>
          <td>mappr.com</td>
        </tr>
      </tbody>
    </table>
  </figure>
  <h2 id="analytics">
    <strong>Analytics</strong>
  </h2>
  <p>
    We use a <strong>cookieless analytics system</strong> for all website
    insights. This means we do not store or access information on your device
    using cookies, whether you are signed out or signed in.
  </p>
  <p>
    Once you log in, we identify your account with these events. This data helps
    us <strong>continually improve the product</strong>, a processing activity
    we conduct based on our legitimate interest as detailed in our Legitimate
    Interest Assessment (LIA).
  </p>
  <p>
    For marketing personalization, you explicitly agree during onboarding, and
    you can opt out at any point. We&#39;re committed to protecting your data
    responsibly and if you have any questions, please do not hesitate to email
    us via <a href="mailto:support@mappr.com">support@mappr.com</a>.
  </p>
  <h2 id="advertising">
    <strong>Advertising</strong>
  </h2>
  <p>We do not use any advertising or remarketing cookies.</p>
  <h2 id="3-how-to-disable-cookies">3. How to disable cookies</h2>
  <p>
    We only use essential and functional cookies, disabling these will lead to
    the site not functionality correctly. You can however override this via your
    browser if you wish.
  </p>
  <p>
    Additionally, all browsers allow cookie settings to be adjusted for enhanced
    privacy control. Below are instructions for disabling cookies in the most
    commonly used browsers:
  </p>
  <ul>
    <li>
      Chrome:
      <a
        href="https://support.google.com/accounts/answer/61416?hl=en&amp;co=GENIE.Platform%3DDesktop"
        target="_blank"
        class="url"
        >https://support.google.com/accounts/answer/61416?hl=en&co=GENIE.Platform%3DDesktop</a
      >
    </li>
    <li>
      Internet Explorer:
      <a
        href="https://support.microsoft.com/es-es/windows/administrar-cookies-en-microsoft-edge-ver-permitir-bloquear-eliminar-y-usar-168dab11-0753-043d-7c16-ede5947fc64d"
        target="_blank"
        class="url"
        >https://support.microsoft.com/es-es/windows/administrar-cookies-en-microsoft-edge-ver-permitir-bloquear-eliminar-y-usar-168dab11-0753-043d-7c16-ede5947fc64d</a
      >
    </li>
    <li>
      Firefox:
      <a
        href="https://support.mozilla.org/es/kb/cookies-informacion-que-los-sitios-web-guardan-en-?redirectlocale=en-US&amp;redirectslug=Cookies"
        target="_blank"
        class="url"
        >https://support.mozilla.org/es/kb/cookies-informacion-que-los-sitios-web-guardan-en-?redirectlocale=en-US&redirectslug=Cookies</a
      >
    </li>
    <li>
      Safari:
      <a
        href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac"
        target="_blank"
        class="url"
        >https://support.apple.com/en-gb/guide/safari/sfri11471/mac</a
      >
    </li>
    <li>
      iOS:
      <a
        href="https://support.apple.com/es-es/105082"
        target="_blank"
        class="url"
        >https://support.apple.com/es-es/105082</a
      >
    </li>
    <li>
      Chrome Mobile:
      <a
        href="https://support.google.com/chrome/answer/114662?hl=es&amp;visit_id=638852434905558389-3710747119&amp;rd=1"
        target="_blank"
        class="url"
        >https://support.google.com/chrome/answer/114662?hl=es&visit_id=638852434905558389-3710747119&rd=1</a
      >
    </li>
  </ul>
  <h2 id="4-cookie-consent">4. Cookie consent</h2>
  <p>
    We only use essential &amp; functional cookies for users, and we do not use
    any advertising or remarketing cookies. So there is no need to accept any
    cookies on the website.
  </p>
  <h2 id="5-retention-period">5. Retention period</h2>
  <p>
    The retention period of cookies varies from 24 hours to 1 year. See the
    table above for exact retention periods (expiry) of certain cookies. Data
    collected through persistent cookies will be retained for a period longer
    than the browsing session on this website. You can find information on the
    retention of data obtained by third parties in their respective policies on
    the processing of collected data.
  </p>
  <h2 id="6-processing-of-personal-data">6. Processing of personal data</h2>
  <p>
    For additional information on the processing of personal data based on
    consent, please refer to the
    <NuxtLink to="/info/privacy-policy"> Privacy Policy </NuxtLink>.
  </p>
  <!-- <h2 id='7-updates'>7. Updates</h2> -->
  <p>
    This policy may be periodically updated to reflect changes based on the
    cookies we use. 2025 “MAPPREXPLORE, S.L.” Total or partial reproduction is
    prohibited. All rights reserved.
  </p>
</template>

<style scoped>
table,
figure {
  text-align: left;
  margin: 0;
  padding: 0;
  font-size: 15px;
}
td {
  padding: 0.5rem 0.5rem;
  border-top: 1px solid var(--color-slate-200);
  align-items: baseline;
}

a {
  color: var(--color-indigo-500);
  text-decoration: none;
}
</style>
